/**
 * CRM-провайдеры меншнов для композера Activity.
 *
 * - scope-меншн (`#`): сделки партнёра — доступен только в partner-треде;
 * - поиск контактных лиц: связанные контакты — в partner- и lead-тредах.
 *
 * Debounce/кэш выдачи обеспечивает ядро; здесь — только доменный поиск.
 */
import {
  CRM_ACTIVITY_ENTITY_TYPES,
} from '@ayocore/activity/common/activity/constants';
import type {
  ActivityContactSearchItem,
  ActivityContactSearchProvider,
  ActivityMentionThreadContext,
  ActivityScopeMentionItem,
  ActivityScopeMentionProvider,
} from '@ayocore/activity/client/extensions';
import {
  contactPersonRepository,
  dealRepository,
  leadRepository,
  partnerRepository,
} from '@ayocore/erp/crm/client/rpc';

const DEAL_MENTION_LIMIT = 12;
const CONTACT_MENTION_LIMIT = 12;

type EntityLookupRow = {
  id?: number | null;
};

type DealMentionRow = {
  id: number | null;
  uuid: string;
  number: string | null;
};

type ContactMentionRow = {
  id: number;
  uuid: string;
  name: string | null;
  position?: string | null;
};

/** Кэш резолва `entity_type:uuid → id` на время жизни клиента. */
const entityIdCache = new Map<string, number>();

async function resolveEntityIdCached(
  cacheKey: string,
  lookup: () => Promise<EntityLookupRow | null>
): Promise<number | null> {
  const cached = entityIdCache.get(cacheKey);
  if (cached && cached > 0) return cached;
  const row = await lookup();
  const id = typeof row?.id === 'number' && row.id > 0 ? row.id : null;
  if (id) entityIdCache.set(cacheKey, id);
  return id;
}

function normalizeDealLabel(row: DealMentionRow): string {
  if (typeof row.id === 'number' && row.id > 0) return String(row.id);
  return row.uuid.slice(0, 8);
}

function threadUuid(ctx: ActivityMentionThreadContext): string {
  return typeof ctx.threadEntityUuid === 'string' ? ctx.threadEntityUuid.trim() : '';
}

/** `#`-меншн: сделки текущего партнёра. */
export const crmDealScopeMentionProvider: ActivityScopeMentionProvider = {
  id: 'crm_deals',
  isAvailable: ctx =>
    ctx.threadEntityType === CRM_ACTIVITY_ENTITY_TYPES.PARTNER && threadUuid(ctx).length > 0,
  search: async (query, ctx): Promise<ActivityScopeMentionItem[]> => {
    const partnerUuid = threadUuid(ctx);
    const partnerId = await resolveEntityIdCached(
      `${CRM_ACTIVITY_ENTITY_TYPES.PARTNER}:${partnerUuid}`,
      () => partnerRepository.findById(partnerUuid)
    );
    if (!partnerId) return [];

    let repository = dealRepository.filterByPartner(partnerId);
    if (query.length > 0) {
      repository = repository.search(query);
    }

    const rows = (await repository
      .orderBy('updated_at', 'desc')
      .limit(DEAL_MENTION_LIMIT)
      .fetch()) as DealMentionRow[];

    return (Array.isArray(rows) ? rows : [])
      .filter(row => typeof row.uuid === 'string' && row.uuid.trim().length > 0)
      .map(row => {
        const label = normalizeDealLabel(row);
        return {
          uuid: row.uuid,
          label,
          searchText: `${label.toLowerCase()} ${typeof row.id === 'number' ? row.id : ''}`,
          entity_type: CRM_ACTIVITY_ENTITY_TYPES.DEAL,
        };
      });
  },
};

/** Поиск контактных лиц, связанных с партнёром или лидом треда. */
export const crmContactSearchProvider: ActivityContactSearchProvider = {
  id: 'crm_contacts',
  isAvailable: ctx =>
    (ctx.threadEntityType === CRM_ACTIVITY_ENTITY_TYPES.PARTNER ||
      ctx.threadEntityType === CRM_ACTIVITY_ENTITY_TYPES.LEAD) &&
    threadUuid(ctx).length > 0,
  search: async (query, ctx): Promise<ActivityContactSearchItem[]> => {
    const isPartnerThread = ctx.threadEntityType === CRM_ACTIVITY_ENTITY_TYPES.PARTNER;
    const entityUuid = threadUuid(ctx);
    const entityId = await resolveEntityIdCached(
      `${ctx.threadEntityType}:${entityUuid}`,
      () => (isPartnerThread ? partnerRepository : leadRepository).findById(entityUuid)
    );
    if (!entityId) return [];

    const repository = isPartnerThread
      ? contactPersonRepository.filterByPartner(entityId)
      : contactPersonRepository.filterByLead(entityId);

    const rows = (await repository
      .orderBy('name', 'asc')
      .limit(CONTACT_MENTION_LIMIT)
      .fetch()) as ContactMentionRow[];

    return (Array.isArray(rows) ? rows : [])
      .filter(row => typeof row.uuid === 'string' && row.uuid.trim().length > 0)
      .filter(row => typeof row.name === 'string' && row.name.trim().length > 0)
      .map(row => {
        const label = (row.name ?? '').trim();
        const positionText = row.position?.trim() ?? '';
        return {
          uuid: row.uuid,
          label,
          searchText: `${label.toLowerCase()} ${positionText.toLowerCase()}`.trim(),
        };
      });
  },
};
